import React, { useEffect, useState } from 'react';
import { Icon } from '../../../../../concepts/resources/Icon';
import { Button, Colors, Input } from '../../../../../ui';
import { ResultInfoForMutation } from '../../../../../ui/ResultInfo';
import { AppNavigator } from '../../../../AppNavigator';
import { useResolve } from '../../../../Container';
import { Copy, Flexer, Title } from '../../../../dashboard/elements/LayoutElements';
import { useSingleSignOnConstants, useSingleSignOnForm, useSingleSignOnMutation } from '../SingleSignOnHooks';
import { CommonFinalStep } from './CommonFinalStep';
import { InputCopyButton } from './InputCopyButton';
import { SetupStep, StepProps } from './SetupStep';
import { SetupStepViewer } from './SetupStepViewer';

const CreateAppStep: React.FC<StepProps> = ({ gotoStep }) => {
    return (
        <SetupStep title="Step 1 — Create Azure Enterprise Application">
            <Copy flush>
                <p>From within the Azure portal:</p>
                <ul>
                    <li>Search for and select <b>Enterprise Applications</b> from the resources palette</li>
                    <li>Click <b>New Application</b></li>
                    <li>Click <b>Create your own application</b></li>
                </ul>
            </Copy>
            <Copy flush>
                <p>On the Create your own application page:</p>
                <ul>
                    <li>Set the name as <b>Metro Retro</b></li>
                    <li>Select <b>Integrate any other application you don&apos;t find in the gallery</b> if not already selected</li>
                    <li>Click <b>Create</b></li>
                </ul>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(1)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

const ConfigureServiceProviderStep: React.FC<StepProps> = ({ gotoStep }) => {
    const { entityId, acsUrl } = useSingleSignOnConstants();

    return (
        <SetupStep title="Step 2 — Service Provider Details for Azure">
            <Copy flush>
                <p>
                    Select <b>Single sign-on</b> from the left menu (or getting started quick link) and select <b>SAML</b> as the sign-in method.
                </p>
            </Copy>
            <Copy flush>
                <p>
                    Under <b>Basic SAML configuration</b>, enter the following settings:
                </p>
            </Copy>
            <Flexer>
                <Input
                    label="Identifier (Entity ID):"
                    value={entityId}
                    after={() => <InputCopyButton value={entityId} />}
                    hint="This identifies your Metro Retro account to Azure."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Reply URL (Assertion Consumer Service URL):"
                    value={acsUrl}
                    after={() => <InputCopyButton value={acsUrl} />}
                    hint="The endpoint your Azure IDP sends users to when they log in."
                />
            </Flexer>
            <Copy flush>
                <p>Leave all other fields in this section blank.</p>
            </Copy>
            <Copy flush>
                <p>
                    Leave the default User Attributes & Claims settings. If the defaults are different or you have changed them, please set them to include:
                </p>
                <ul>
                    <li>givenname <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> user.givenname</li>
                    <li>surname <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> user.surname</li>
                    <li>emailaddress <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> user.mail</li>
                </ul>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(2)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

// Step 3 - Configure Azure Identity Provider details
const ConfigureIdentityProviderStep: React.FC<StepProps> = ({ form, mutation, gotoStep }) => {
    const { draft, draftDirty, patchDraft } = form;

    useEffect(() => {
        mutation.reset();
    }, [draft]);

    const onSave = () => {
        mutation.mutate(draft);
    };

    return (
        <SetupStep title="Step 3 — Azure Identity Provider Details">
            <Copy flush>
                <p>
                    Next, download the Base64 encoded Certificate file from Section 3 of the Azure SAML configuration.
                    Make a note of the Login URL and Azure AD Identifier from Section 4.
                    Enter these values below:
                </p>
            </Copy>
            <Flexer>
                <Input
                    label="Login URL:"
                    value={draft.entryPoint}
                    error={form.errors.entryPoint}
                    onChange={x => patchDraft({ entryPoint: x })}
                    hint="The Login URL from Azure (Azure's SSO URL)."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Microsoft Entra Identifier:"
                    value={draft.issuer}
                    error={form.errors.issuer}
                    onChange={x => patchDraft({ issuer: x })}
                    hint="The Microsoft Entra Identifier (Azure's Entity ID)."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="X.509 Certificate:"
                    multiline={{ rows: 3, resize: 'vertical' }}
                    value={draft.certificate}
                    error={form.errors.certificate}
                    onChange={x => patchDraft({ certificate: x })}
                    hint="The text content of the Base64 certificate file from Azure."
                />
            </Flexer>
            <Flexer>
                <Copy flush>
                    To get the X.590 certficate, click Download next to Certificate (Base64) in the Token Signing Certificate section.
                </Copy>
            </Flexer>
            <Flexer>
                <ResultInfoForMutation mutation={mutation} onSuccess="Settings saved successfully." />
            </Flexer>
            {!mutation.isSuccess && <Flexer flex="row" justify="end">
                <Button disabled={mutation.isLoading || !draftDirty} onAction={onSave}>
                    Save
                </Button>
            </Flexer>}
            {mutation.isSuccess && <>
                <Flexer flex="row" justify="end">
                    <Button variant="primary" onAction={() => gotoStep(3)}>
                        Next
                    </Button>
                </Flexer>
            </>}
        </SetupStep>
    );
};

export const SetupAzureSingleSignOnView = React.memo(() => {

    const nav = useResolve(AppNavigator);
    const [activeStep, setActiveStep] = useState(0);
    const steps = 4; // 4 steps for Azure

    const form = useSingleSignOnForm();
    const mutation = useSingleSignOnMutation();

    const gotoStep = (step: number | 'end') => {
        if (step === 'end') {
            nav.go('~/manage/sso');
        }
        else if (step >= 0 && step < steps) {
            setActiveStep(step);
        }
    };

    const stepProps: StepProps = {
        form,
        mutation,
        currentStep: activeStep,
        gotoStep,
    };

    return <Flexer flex="column" gap="2" maxWidth="54rem">
        <Title size="lg" color={Colors.text}>Azure Setup</Title>
        <SetupStepViewer steps={steps} activeIndex={activeStep} onStep={gotoStep} />
        {activeStep === 0 && <CreateAppStep {...stepProps} />}
        {activeStep === 1 && <ConfigureServiceProviderStep {...stepProps} />}
        {activeStep === 2 && <ConfigureIdentityProviderStep {...stepProps} />}
        {activeStep === 3 && <CommonFinalStep {...stepProps} />}
    </Flexer>;
});
